import React, { Fragment, useEffect, useState, useCallback } from "react";
import { Tile } from "components/UIComponents/Tile";
import styles from "./RiskAssessments.module.scss";
import {
  getClientRiskAssessmentQuestions,
  getMatterRiskAssessmentQuestions,
  saveClientRiskAssessmentQuestions,
  saveMatterRiskAssessmentQuestions,
  updateOnboarding,
} from "api";

import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { toast } from "react-toastify";
import LoadingScreen from "components/common/Loading";
import { DateFormat } from "components/common/formatter";
import ErrorCard from "components/Error/ErrorCard";

export const RiskAssessment = ({
  moveNext,
  formData,
  matterId,
  clientId,
  setHandlefn,
  selectedView,
  type,
}) => {
  const [data, setData] = useState({});
  const [savedAnswers, setSavedAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("");
  const questionRefs = React.useRef([]);

  const getMatterAssessmentData = async () => {
    try {
      setLoading(true);
      const data = await getMatterRiskAssessmentQuestions(
        matterId || formData.matterDetails?.matterId
      );
      setData(data?.data?.data);
    } catch (error) {
      toast.error("Failed to fetch matter risk assessment data");
    } finally {
      setLoading(false);
    }
  };

  const saveAnswer = (id, data) => {
    const answers = savedAnswers.filter((answer) => answer?.questionId !== id);
    const { questionId, answer, comments } = data;
    setSavedAnswers([...answers, { questionId, answer, comments }]);
  };

  const getClientAssessmentData = async () => {
    try {
      setLoading(true);
      const data = await getClientRiskAssessmentQuestions(
        clientId || formData.clientData?.clientId
      );
      setData(data?.data?.data);
    } catch (error) {
      toast.error("Failed to fetch client risk assessment data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setView(selectedView);
  }, [selectedView]);

  const validateAnswers = (questions, savedAnswers) => {
    if (!questions || questions.length === 0) return { valid: true };

    const missingQuestions = questions
      .map((q, idx) => ({
        idx: idx + 1,
        answered: savedAnswers.some(
          (a) => a.questionId === q.questionId && a.answer
        ),
      }))
      .filter((q) => !q.answered)
      .map((q) => q.idx);

    if (missingQuestions.length > 0) {
      return {
        valid: false,
        message: `Please answer question${
          missingQuestions.length > 1 ? "s" : ""
        } ${missingQuestions.join(", ")}.`,
      };
    }

    return { valid: true };
  };

  const submitAnswers = async () => {
    if (!data?.questions) return { success: false };

    // Step 1: validate answers
    const validation = validateAnswers(data.questions, savedAnswers);
    if (!validation.valid) {
      toast.error(validation.message);
      return { success: false };
    }
    try {
      const payload = {
        data: {
          riskAssessementId: data.riskAssessementId,
          answers: savedAnswers,
        },
      };

      const isClient = clientId || view === "client";
      const apiCall = isClient
        ? saveClientRiskAssessmentQuestions
        : saveMatterRiskAssessmentQuestions;

      const id = isClient
        ? clientId || formData.clientData?.clientId
        : matterId || formData.matterDetails?.matterId;

      const result = await apiCall(id, payload);
      const submissionResponse = result?.data?.data;

      // 🔄 Refresh data after save
      if (isClient && type !== "OnBoarding") {
        await getClientAssessmentData();
      } else if (type !== "OnBoarding") {
        await getMatterAssessmentData();
      }

      toast.success("Questionnaire Updated");
      return { success: true, data: submissionResponse };
    } catch (error) {
      console.error(error);
      toast.error("Unexpected Error Occurred");
      return { success: false };
    }
  };

  const handleNext = async () => {
    setLoading(true);
    try {
      let submissionId = data.submissionId;

      // Only submit answers if no submissionId exists yet
      if (!submissionId) {
        const { success, data: submissionResponse } = await submitAnswers();
        if (!success || !submissionResponse) return;
        submissionId = submissionResponse.submissionId;
      }

      // Build updatePayload based on view
      const updatePayload =
        selectedView === "client"
          ? { clientRiskSubmissionId: submissionId }
          : { matterRiskSubmissionId: submissionId };

      await updateOnboarding(updatePayload, formData.onboardingId);

      // Build updated risk assessment state
      const updatedRiskAssessment =
        selectedView === "client"
          ? {
              matterData: formData.riskAssessment?.matterData,
              clientData: !data.submissionId
                ? { submissionId }
                : formData.riskAssessment?.clientData,
            }
          : {
              matterData: !data.submissionId
                ? { submissionId }
                : formData.riskAssessment?.matterData,
              clientData: formData.riskAssessment?.clientData,
            };

      moveNext({}, { riskAssessment: updatedRiskAssessment });
    } catch (error) {
      console.error(error);
      toast.error("Unexpected Error Occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId || view === "client") {
      getClientAssessmentData();
    } else if (matterId || view === "matter") {
      getMatterAssessmentData();
    }
  }, [view]);

  const formattedDate = DateFormat(data?.submittedOn);
  return (
    <Fragment>
      {(!data?.questions || data.questions.length === 0) && !loading ? (
        <div className={`${!setHandlefn && "w-100"}`}>
          <ErrorCard
            icon="⚠️"
            title="No Risk Assessment Questions Found"
            description="This matter currently has no associated risk assessment questions."
            isClientOnboarding={setHandlefn}
          />
        </div>
      ) : (
        <div className={`${!formData && "container pt-3"} h-100`}>
          <Tile
            heading="Risk Assessment"
            className={`"mb-2 px-4 py-2 overflow-auto h-100 ${
              setHandlefn && "bg-transparent border-0 border w-100 shadow-none"
            }`}
            height={!formData ? "580px" : ""}
            headingItems={
              <div className="d-flex align-items-center gap-2">
                {formData && !selectedView && (
                  <div className={`${styles["button-group"]}`}>
                    <ButtonGroup width="100%">
                      <Button
                        className={`rounded-pill border-0 mx-1`}
                        style={{
                          color: view === "matter" && "white",
                          backgroundColor:
                            view === "matter" ? "#241CCA" : "#FFFFFF",
                        }}
                        onClick={() => setView("matter")}
                      >
                        Matter
                      </Button>
                      <Button
                        className="rounded-pill border-0 mx-1"
                        style={{
                          color: view === "client" && "white",
                          backgroundColor:
                            view === "client" ? "#241CCA" : "#FFFFFF",
                        }}
                        onClick={() => setView("client")}
                      >
                        Client
                      </Button>
                    </ButtonGroup>
                  </div>
                )}

                <div className={`d-flex gap-2 ${styles["header-text"]}`}>
                  {data?.submissionId !== null &&
                    data?.submissionId !== undefined && (
                      <>
                        <div className="">
                          <span
                            style={{
                              color: "black",
                              fontWeight: 600,
                            }}
                            className="me-2"
                          >
                            Submitted By:
                          </span>
                          <span className={`${styles["detail-text"]}`}>
                            {data?.submittedBy}
                          </span>
                        </div>
                        <div
                          style={{
                            color: "black",
                            fontWeight: 600,
                          }}
                        >
                          |
                        </div>
                        <div className="me-3">
                          <span
                            style={{
                              color: "black",
                              fontWeight: 600,
                            }}
                            className="me-2"
                          >
                            Submitted On:
                          </span>
                          <span className={`${styles["detail-text"]}`}>
                            {formattedDate}
                          </span>
                        </div>
                      </>
                    )}
                  <div>
                    <span
                      style={{
                        color: "black",
                        fontWeight: 600,
                      }}
                      className="me-2"
                    >
                      Status:
                    </span>
                    <span
                      style={{
                        color:
                          data?.status === "Pass"
                            ? "green"
                            : data?.status === "Failed"
                            ? "red"
                            : data.status === "Not Submitted"
                            ? "black"
                            : "red",
                      }}
                    >
                      {data?.status}
                    </span>
                  </div>
                </div>
              </div>
            }
          >
            <div
              style={{
                height: formData ? "calc(100% - 70px)" : "auto",
              }}
              className="container-fluid"
            >
              <div
                className="d-flex flex-column  justify-content-between "
                style={{
                  height: !formData ? "495px" : "100%",
                }}
              >
                <div
                  className="overflow-auto"
                  style={{ height: "calc(100% - 55px)" }}
                >
                  <div>
                    {data?.questions?.map((question, index) => (
                      <QuestionTile
                        data={{
                          ...question,
                          _ref: (el) => (questionRefs.current[index] = el),
                        }}
                        key={index}
                        disabled={data?.submissionId !== null}
                        saveAnswer={saveAnswer}
                        index={index}
                        answer={question?.answer?.answer}
                        view={view}
                      />
                    ))}
                  </div>
                </div>
                {setHandlefn ? (
                  <div
                    className={` px-3  rounded-3 border border-1 border-bottom-0 border-light shadow-sm`}
                    style={{
                      height: "55px",
                      boxShadow: "0 0 16px rgba(44, 62, 80, 0.07);",
                    }}
                  >
                    <Button
                      className="mt-2 common-btn"
                      style={{ float: "right" }}
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </div>
                ) : (
                  <div className="row p-2 align-self-end ">
                    <Button
                      className="common-btn"
                      style={{
                        maxWidth: "80px",
                      }}
                      disabled={
                        data.submissionId !== null ||
                        data.riskAssessementId === null
                      }
                      onClick={submitAnswers}
                    >
                      Submit
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Tile>
          {loading && <LoadingScreen />}
        </div>
      )}
    </Fragment>
  );
};

const QuestionTile = ({
  data,
  index,
  answer,
  saveAnswer,
  disabled,
  view,
  showValidationError,
}) => {
  const [checkBoxesValues, setCheckBoxesValues] = useState({
    firstValue: answer === "Yes" ? true : false,
    secondValue: answer === "No" ? true : false,
    thirdValue: answer === "Unknown" ? true : false,
  });
  const [comments, setComments] = useState(data?.answer?.comments || "");
  const [commentError, setCommentError] = useState("");

  const QUESTIONS_LEGEND = {
    firstValue: "Yes",
    secondValue: "No",
    thirdValue: "Unknown",
  };

  const commentsForArr = (
    typeof data.commentsFor === "string"
      ? data.commentsFor.split(",").map((v) => v.trim().toLowerCase())
      : []
  ).filter(Boolean);

  const currentAnswer = Object.keys(checkBoxesValues).find(
    (k) => checkBoxesValues[k]
  );
  const selectedAnswer = QUESTIONS_LEGEND[currentAnswer];

  useEffect(() => {
    setCheckBoxesValues({
      firstValue: answer === "Yes" ? true : false,
      secondValue: answer === "No" ? true : false,
      thirdValue: answer === "Unknown" ? true : false,
    });
    setComments(data?.answer?.comments || "");
  }, [answer, view, data?.answer]);

  const handleChange = (e) => {
    if (disabled) return;
    const label = e?.target?.name;
    const currentValues = checkBoxesValues;
    if (e.value) {
      Object.keys(checkBoxesValues).forEach((name) => {
        if (name !== label) {
          currentValues[name] = false;
        }
      });
      currentValues[label] = e.value;
    } else if (!e.value) {
      currentValues[label] = false;
    }
    setCheckBoxesValues({ ...currentValues });
    setComments("");
    saveAnswer(data.questionId, {
      questionId: data?.questionId,
      commentsFor: data?.commentsFor,
      answer: QUESTIONS_LEGEND[label],
      comments: "",
    });
  };

  const handleCommentChange = (val) => {
    setComments(val);
    setCommentError("");
    saveAnswer(data.questionId, {
      questionId: data?.questionId,
      commentsFor: data?.commentsFor,
      answer: selectedAnswer,
      comments: val,
    });
  };

  React.useImperativeHandle(data._ref, () => ({
    validate: () => {
      if (!comments.trim()) {
        setCommentError("Comment is required.");
        return false;
      }
      setCommentError("");
      return true;
    },
  }));

  return (
    <div className="d-flex flex-column">
      <div
        className={`${styles["question-box"]} p-2`}
        style={{
          backgroundColor: index % 2 === 0 && "#F9F9FF",
          minHeight: 110,
          transition: "min-height 0.2s",
        }}
      >
        <div
          className={`${styles["question-text"]} truncate`}
          style={{
            minWidth: 320,
            maxWidth: "60%",
          }}
        >
          {data?.question}
        </div>
        <div
          className="d-flex flex-column align-items-start"
          style={{ minWidth: 320 }}
        >
          <div className="d-flex align-items-center mb-2 ">
            <div className=" me-4 ">
              <Checkbox
                onChange={handleChange}
                name="firstValue"
                label={"Yes"}
                labelClassName={styles["checkboxes"]}
                style={{
                  height: "17px",
                  fontWeight: 600,
                  width: "17px",
                }}
                value={checkBoxesValues["firstValue"]}
                disabled={disabled}
              />
            </div>
            <div className="me-4">
              <Checkbox
                onChange={handleChange}
                name="secondValue"
                label={"No"}
                style={{
                  height: "17px",
                  fontWeight: "600 !important",
                  fontFamily: "Montserrat",
                  width: "17px",
                }}
                labelClassName={styles["checkboxes"]}
                value={checkBoxesValues["secondValue"]}
                disabled={disabled}
              />
            </div>
            {data.showUnknown && (
              <div className="">
                <Checkbox
                  label={data.unknownText}
                  onChange={handleChange}
                  name="thirdValue"
                  style={{
                    height: "17px",
                    width: "17px",
                    fontWeight: 600,
                  }}
                  labelClassName={`${styles["checkboxes"]} text-wrap`}
                  value={checkBoxesValues["thirdValue"]}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
          <div className="mt-1 w-100">
            <input
              type="text"
              className={`bg-light border border-1 shadow-sm rounded-3 px-3 py-2 custom-comment-input ${styles["comment-input"]}`}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: "16px",
                boxShadow: "none",
                padding: 0,
              }}
              placeholder="Type comment here..."
              value={comments}
              onChange={(e) => handleCommentChange(e.target.value)}
              disabled={disabled}
            />
            {commentError && (
              <div style={{ color: "red", fontSize: 13, marginTop: 4 }}>
                {commentError}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
